<template>
  <form v-if="account">
    <div class="row mb-2">
      <div class="col-sm-12">

        <div class="row mt-2">
          <div class="col-sm-12 d-flex align-items-center p-2 mt-3 justify-content-center">
            <AccountWidget show-icon :account="account"></AccountWidget>
          </div>
        </div>

        <div class="row mt-2">
          <label for="amount" class="col-sm-12 col-form-label text-capitalize-first">{{ helper.trans("labels.amount") }} <a href="#" @click.prevent="form.amount = numberFormatter.formatNumber(account.sum.amount, account.sum.subunit)"><small>{{ numberFormatter.formatNumber(account.sum.amount, account.sum.subunit) }}</small></a></label>
          <div class="col-sm-12">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`amount`)" >
              <input v-model="form.amount"
                     :class="parentProps.class"
                     type="text" class="form-control " id="amount">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label for="traders" class="col-sm-12 col-form-label text-capitalize-first">{{ helper.trans("labels.trader") }}</label>
          <div class="col-sm-12">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`targetAccountId`)">
              <Multiselect id="traders"
                           class=""
                           :class="parentProps.class"
                           v-model="form.account"
                           :options="tradersOptions"
                           :custom-label="accountLabel"
                           track-by="id"
                           select-label="Select"
                           placeholder=""
                           open-direction="bottom"
                           :multiple="false"
                           :searchable="true"
                           :loading="isTradersSelectLoading"
                           :internal-search="false"
                           :clear-on-select="false"
                           :close-on-select="true"
                           :options-limit="100"
                           :limit="1000"
                           :show-no-results="false"
                           :hide-selected="true"
                           @search-change="asyncFindTraders"
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>
      </div>
    </div>

  </form>
</template>

<script lang="ts">
import {defineComponent, toRefs, reactive, computed, PropType, Prop, watch} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import AccountDTO from "@/models/AccountDTO";
import {numberFormatter} from "@/service/NumberFormatter";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import TransferFormDTO from "@/models/form/TransferFormDTO";
import AccountTransferRequestDTO from "@/models/request/AccountTransferRequestDTO";
import PaginationDTO from "@/models/PaginationDTO";
import Multiselect from "vue-multiselect";
import TraderAccountDTO from "@/models/TraderAccountDTO";
import AccountWidget from "@/components/AccountWidget.vue";

const api = new APIClient();
const notification = useNotification()

export default defineComponent({
  props: {
    account: {
      type: Object as PropType<AccountDTO>,
      default: null
    } as Prop<AccountDTO>
  },
  components: {
    AccountWidget,
    FormInputWrapper,
    Multiselect
  },
  setup(props) {
    const store = useStore();
    const state = reactive({
      errors: {} as ValidationErrorDTO[]|any,
      form: {} as TransferFormDTO,
      traderAccounts: [] as TraderAccountDTO[],
      isTradersSelectLoading: false,
    });

    watch(() => props.account, async () => {
      state.form = {
        amount: null,
        account: null,
      }  as TransferFormDTO
      state.errors = [];
      if (props.account !== null) {
        asyncFindTraders("")
      }
    })

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!error.field) {
          notification.notify({type: 'error', title: error.message});
          continue;
        }
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const getForm = (): TransferFormDTO => {
      return state.form
    }

    const asyncFindTraders = async (query: string) => {
      state.isTradersSelectLoading = true
      state.traderAccounts = (await api.traderAccounts({limit: 100, offset: 0} as PaginationDTO, {query: query, currency: props.account?.currency})).items;
      state.isTradersSelectLoading = false
    }

    const tradersOptions = computed(() => {
      if (!state.form) {
        return [];
      }

      return state.traderAccounts.filter((traderAccount) => traderAccount.account.id !== props.account?.id);
    })

    const setForm = (form: TransferFormDTO) => {
      state.form = {
        ...form,
      }
    }

    const accountLabel = (opt: TraderAccountDTO) => {
      return `${opt.traderEmail} - ${numberFormatter.formatNumber(opt.account.sum, opt.account.sum.subunit)} ${opt.account.sum.currency}`
    }

    const save = (): Promise<any> => {
      setErrors([]);
      const form = getForm();

      return api.accountTransferCreate(props.account!.id, {
        targetAccountId: form.account?.account.id,
        amount: form.amount ? form.amount.replace(" ", '') : ''
      } as AccountTransferRequestDTO)
        .then((resp) => {
          notification.notify({title: helper.trans('transfer.notice.created')});
        })
        .catch((e: any) => {
          if (e.response && e.request && e.request.status === 400 && e.response.data) {
            setErrors(e.response.data)
          } else {
            console.log(e);
          }
          throw e;
        })
    }

    return {
      ...toRefs(state),
      tradersOptions,
      getForm,
      setForm,
      save,
      store,
      helper,
      getErrors,
      numberFormatter,
      asyncFindTraders,
      accountLabel
    }
  },
})
</script>
